import React from "react";
import { Navigate } from "react-router-dom";
import { CalendarDays, Sparkles, Star } from "lucide-react";
import DailyHoroscope from "@/components/DailyHoroscope";
import { useAuth } from "@/context/AuthContext";

// This is an in-dashboard navigation restriction, not a replacement for
// authenticated API authorization; the public landing retains its horoscope.
export const HOROSCOPE_ROLES = Object.freeze(["user", "student", "consultant", "creator"]);
const PRIVILEGED_ROLES = new Set(["director", "admin", "support"]);

export function canViewDashboardHoroscope(accessMap) {
  const roles = Array.isArray(accessMap?.roles) ? accessMap.roles : [];
  if (roles.length === 0 || roles.some((role) => PRIVILEGED_ROLES.has(role))) return false;
  return roles.some((role) => HOROSCOPE_ROLES.includes(role));
}

export function HoroscopeRoleGuard({ children }) {
  const { accessMap } = useAuth();
  if (!canViewDashboardHoroscope(accessMap)) {
    return <Navigate to="/app/inicio" replace />;
  }
  return children;
}

const todayBogota = () => new Intl.DateTimeFormat("es-CO", {
  dateStyle: "full", timeZone: "America/Bogota",
}).format(new Date());

export default function HoroscopoDiario() {
  return (
    <div className="w-full min-w-0 max-w-6xl mx-auto pb-5" data-testid="kd9-dashboard-horoscope">
      <header className="rounded-2xl p-5 sm:p-7 mb-4"
        style={{
          background: "linear-gradient(128deg, var(--kd9-card), var(--kd9-bg-2))",
          border: "1px solid var(--kd9-border)",
        }}>
        <div className="flex items-center gap-2 mb-2 font-mont text-[10px] tracking-[.2em] uppercase"
             style={{ color: "var(--kd9-accent)" }}>
          <Sparkles size={17} aria-hidden="true" /> KHAOS DELTA 9 · HORÓSCOPO
        </div>
        <h1 className="font-cinzel kd9-gold-text text-2xl sm:text-3xl leading-tight">Tu horóscopo diario</h1>
        <p className="font-mont text-sm mt-3 leading-relaxed" style={{ color: "var(--kd9-text-soft)" }}>
          Los doce signos, su radar energético y su orientación del día. Selecciona tu signo para explorar su lectura.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-4 text-xs" style={{ color: "var(--kd9-text-soft)" }}>
          <CalendarDays size={17} aria-hidden="true" />
          <time>{todayBogota()}</time>
          <Star size={15} className="ml-2" aria-hidden="true" />
          Actualización diaria · Hora de Colombia
        </div>
      </header>

      {/* The exact same read-only horoscope source used by the public landing.
          Never clone an edition, overwrite the Director's edits or fabricate readings. */}
      <DailyHoroscope embedded inDashboard showOracleWall={false} />

      <p className="font-mont text-xs leading-relaxed text-center px-4"
         style={{ color: "var(--kd9-text-soft)" }}>
        Orientación simbólica de bienestar y reflexión. No sustituye una valoración médica, psicológica o financiera.
      </p>
    </div>
  );
}
